// Global Instructions Rule Applied!
import React, { useState } from 'react'
import { List, Spin, Typography } from 'antd'
import { showErrorToast } from '../../../utils/errorToast.tsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowUpRightFromSquare, faPaperclip } from '@fortawesome/free-solid-svg-icons'
import { getSignedUrl, FileListItem } from '../../../services/filesService.ts'
import { getAccessToken } from '../../../auth/authService'
import { store } from '../../../../state_manager/store.ts'
import { text, dateText, bytesText, externalHref } from '../lib/format.ts'

/**
 * Read-only attachment rows from a `file_urls_*` table. `type='url'` rows are plain
 * links to the pasted URL; `type='storage'` rows resolve a short-lived signed URL on
 * tap. The viewer window is opened synchronously inside the tap gesture (iOS Safari
 * blocks window.open after an await) and pointed at the signed URL once it arrives —
 * the signed URL expires in seconds, so it can't be pre-resolved at render time.
 */
export function FileList({ files }: { files: FileListItem[] }): React.JSX.Element {
  const [openingId, setOpeningId] = useState<string | null>(null)

  if (files.length === 0) return <Typography.Text type="secondary">No files.</Typography.Text>

  const openStorageFile = async (file: FileListItem) => {
    if (openingId) return
    const popup = window.open('', '_blank')
    setOpeningId(file.id)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await getSignedUrl(file.file_path, token)
      if (popup) {
        popup.location.replace(res.signed_url)
      } else {
        window.open(res.signed_url, '_blank')
      }
    } catch {
      if (popup) popup.close()
      showErrorToast('Could not open the file. Try again.')
    } finally {
      setOpeningId(null)
    }
  }

  return (
    <List
      size="small"
      dataSource={files}
      rowKey={(f: FileListItem) => String(f.id)}
      renderItem={(f: FileListItem) => {
        const isUrl = f.type === 'url'
        const meta = [isUrl ? 'Link' : bytesText(f.file_size_bytes), f.created_at ? dateText(f.created_at) : null]
          .filter(Boolean)
          .join('  ·  ')
        const body = (
          <List.Item.Meta
            avatar={
              openingId === f.id ? (
                <Spin size="small" style={{ marginTop: 2 }} />
              ) : (
                <FontAwesomeIcon icon={isUrl ? faArrowUpRightFromSquare : faPaperclip} style={{ color: '#1890ff', marginTop: 4 }} />
              )
            }
            title={<span style={{ wordBreak: 'break-word' }}>{text(f.file_name)}</span>}
            description={meta || undefined}
          />
        )
        if (isUrl) {
          return (
            <List.Item style={{ paddingLeft: 4, paddingRight: 4 }}>
              <a
                href={externalHref(f.file_path)}
                target="_blank"
                rel="noreferrer"
                style={{ display: 'block', width: '100%', color: 'inherit' }}
              >
                {body}
              </a>
            </List.Item>
          )
        }
        return (
          <List.Item onClick={() => void openStorageFile(f)} style={{ cursor: 'pointer', paddingLeft: 4, paddingRight: 4 }}>
            {body}
          </List.Item>
        )
      }}
    />
  )
}
